import {GameStateEnums} from '../../../../../../shared/GameEnums'
import {UIGameMapHighlightController} from './UIGameMapHighlightController'
import {ArrayUtils} from '@colonist/utils'
import {UIGameManager} from '../../UIGameManager'
import {PIXI} from '@colonist/pixi'
import {UIGameSpriteHandler} from '../../../UIGameSpriteHandler'
import {UIGameMapHighlightParameters} from '../UIGameMapHighlightView'
import {UIGameActionConfirmationParameters} from '../../ui_game_hover_actions/UIHoverActionController'
import {HexEdge} from '@colonist/game-mechanic-validators'
import {ActionState, TileEdgeDirection, TurnState} from '@colonist/game-states'
import UIGameEventEnums = GameStateEnums.UIGameEventEnums

export class UIGameMapTileEdgeHighlightController extends UIGameMapHighlightController {
    protected roadEdges: number[]
    protected shipEdges: number[]

    constructor(uiGameManager: UIGameManager) {
        super(uiGameManager)
        this.roadEdges = []
        this.shipEdges = []
    }

    onEvent(data: any, eventType: GameStateEnums.UIGameEventEnums): void {
        super.onEvent(data, eventType)
        switch(eventType) {
            case UIGameEventEnums.SetRoadEdgeHighlight: this.setRoadEdges(data, true); break
            case UIGameEventEnums.SetShipEdgeHighlight: this.setShipEdges(data, true); break
            case UIGameEventEnums.BeginnerModeActionUpdated: this.onBeginnerModeActionUpdate(data); break
            default: break
        }
    }

    protected setRoadEdges(highlightedIndexes: number[], updateHighlights: boolean = false) {
        this.roadEdges = highlightedIndexes
        if(updateHighlights) this.updateHighlights()
    }

    protected setShipEdges(highlightedIndexes: number[], updateHighlights: boolean = false) {
        this.shipEdges = highlightedIndexes
        if(updateHighlights) this.updateHighlights()
    }

    protected updateHighlights() {
        this.removeHighlights()

        if(this.roadEdges.length > 0 || this.shipEdges.length > 0) {
            this.confirmActionController.setupInvisibleBackground(() => {
                this.startAnimation()
                this.hideBeginnerHintInformation()
            })
        }

        this.highlightRoadEdges()
        this.highlightShipEdges()

        if(this.highlightViews.length > 0) {
            this.uiGameManager.mapController.mapView.sortChildren()
            this.startAnimation()
        }
    }

    private highlightRoadEdges() {
        for(const edgeIndex of this.roadEdges) {
            this.highlightEdge(this.uiGameManager.gameState.mapState.tileState.tileEdges[edgeIndex].hexEdge, edgeIndex)
        }
    }

    private highlightShipEdges(): void {
        for(const edgeIndex of this.shipEdges) {
            if(ArrayUtils.elementInArray(this.roadEdges, edgeIndex)) continue
            this.highlightEdge(this.uiGameManager.gameState.mapState.tileState.tileEdges[edgeIndex].hexEdge, edgeIndex)
        }
    }

    protected getActionForEdgeHighlight(highlightParameters: UIGameMapHighlightParameters, index: number) {
        return () => {
            if(!this.isAllowedPlacementIndex(index)) return this.uiGameManager.eventController.sendEvent(undefined, GameStateEnums.UIGameEventEnums.BeginnerModeForceOpenGuideBubble)

            const confirmationParameters = this.getConfirmationParameters(index)
            const skipConfirmation = this.uiGameManager.gameController.currentState.turnState !== TurnState.InitialPlacement && confirmationParameters.length <= 1
            if(skipConfirmation) return this.confirmedEdgeSelection(index)

            const buttonSize = highlightParameters.size * 1.75 * this.buttonScaleForMobile
            const highlightCenter = new PIXI.Point(highlightParameters.x + highlightParameters.size / 2, highlightParameters.y + highlightParameters.size / 2)
            const buttonX = highlightCenter.x - buttonSize / 2
            const buttonY = highlightCenter.y - buttonSize * 1.5
            const cancelAction = () => this.startAnimation()
            this.destroyBeginnerHintView()
            this.endAnimation()
            this.confirmActionController.showActionConfirmation(highlightParameters, buttonX, buttonY, buttonSize, cancelAction, confirmationParameters, index)
        }
    }

    protected getEdgeHighlightParameters(edge: HexEdge, edgeIndex: number): UIGameMapHighlightParameters {
        const endPoint1 = edge.endPoints()[0].toPixel(this.uiGameManager.mapController.mapView.mapCenter, this.uiGameManager.mapController.mapView.hexagonHeight / 2)
        const endPoint2 = edge.endPoints()[1].toPixel(this.uiGameManager.mapController.mapView.mapCenter, this.uiGameManager.mapController.mapView.hexagonHeight / 2)

        const size = Math.sqrt((endPoint1.x - endPoint2.x) * (endPoint1.x - endPoint2.x) + (endPoint1.y - endPoint2.y) * (endPoint1.y - endPoint2.y)) / 2 * this.circlesScaleForMobile

        const centerX = (endPoint1.x + endPoint2.x) / 2
        const centerY = (endPoint1.y + endPoint2.y) / 2

        const highlight: UIGameMapHighlightParameters = {
            x: centerX - size / 2,
            y: centerY - size / 2,
            size: size,
            index: edgeIndex,
        }

        return highlight
    }

    protected confirmedEdgeSelection(index: number) {
        switch(this.uiGameManager.gameController.currentState.actionState) {
            case ActionState.PlaceRoad:
            case ActionState.PlaceRoadForFree: this.uiGameManager.socketGameSend.confirmBuildRoad(index); break
            case ActionState.InitialPlacementRoadPlacement:
                this.uiGameManager.socketGameSend.confirmBuildRoad(index)
                this.uiGameManager.socketGameSend.confirmBuildShip(index)
                break
            case ActionState.Place2MoreRoadBuilding:
            case ActionState.Place1MoreRoadBuilding:
                this.uiGameManager.socketGameSend.confirmBuildRoad(index)
                this.uiGameManager.socketGameSend.confirmBuildShip(index)
                break
            case ActionState.PlaceShip:
            case ActionState.PlaceShipForFree: this.uiGameManager.socketGameSend.confirmBuildShip(index); break
            case ActionState.SelectShipToMove: this.uiGameManager.socketGameSend.selectedShipToMove(index); break
            case ActionState.MoveShip: this.uiGameManager.socketGameSend.confirmMoveShip(index); break
            case ActionState.SelectRoadToRemove: this.uiGameManager.socketGameSend.confirmRemoveRoad(index); break
            default: break
        }
    }

    private highlightEdge(edge: HexEdge, index: number) {
        const highlightParameters = this.getEdgeHighlightParameters(edge, index)
        const action = this.getActionForEdgeHighlight(highlightParameters, index)
        this.highlight(highlightParameters, action)
    }

    private getConfirmationParameters(index: number): UIGameActionConfirmationParameters[] {
        const confirmationParameters: UIGameActionConfirmationParameters[] = []
        if(ArrayUtils.elementInArray(this.roadEdges, index)) {
            confirmationParameters.push({
                texture: UIGameSpriteHandler.getRoadForPlayer(this.uiGameManager.gameController.myColor),
                canTakeAction: true,
                bankActionAmount: undefined,
                action: (placementIndex: number) => this.uiGameManager.socketGameSend.confirmBuildRoad(placementIndex),
                resourcesToBuild: [],
            })
        }
        if(ArrayUtils.elementInArray(this.shipEdges, index)) {
            confirmationParameters.push({
                texture: UIGameSpriteHandler.getShipForPlayer(this.uiGameManager.gameController.myColor, TileEdgeDirection.NorthWest),
                canTakeAction: true,
                bankActionAmount: undefined,
                action: (placementIndex: number) => this.uiGameManager.socketGameSend.confirmBuildShip(placementIndex),
                resourcesToBuild: [],
            })
        }
        return confirmationParameters
    }
}
